import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { defaultWorkingHours, withDefaultWorkingHours } from "../utils/workingHours";

const WorkingHoursContext = createContext(null);
const API_URL = import.meta.env.VITE_API_URL || "https://api.endoac.ro";

export function WorkingHoursProvider({ children }) {
  const { token, currentUser } = useAuth();
  const [workingHours, setWorkingHours] = useState(defaultWorkingHours());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isAdmin = currentUser?.role === "admin";

  const authHeaders = useMemo(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const resetToDefault = useCallback(() => {
    setWorkingHours(defaultWorkingHours());
  }, []);

  const fetchWorkingHours = useCallback(async () => {
    if (!token) {
      resetToDefault();
      return null;
    }

    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_URL}/api/users/me/working-hours`, {
        headers: {
          ...authHeaders,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu am putut încărca programul de lucru");
      }
      const next = withDefaultWorkingHours(data?.data?.workingHours);
      setWorkingHours(next);
      return next;
    } catch (err) {
      console.error("Eroare working-hours:", err);
      setError(err.message);
      resetToDefault();
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin, authHeaders, resetToDefault]);

  const saveWorkingHours = useCallback(
    async (payload) => {
      if (!token || !isAdmin) {
        throw new Error("Nu ai permisiunea necesară");
      }
      setSaving(true);
      setError("");
      try {
        const res = await fetch(`${API_URL}/api/users/me/working-hours`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify({ workingHours: payload }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu am putut salva programul");
        }
        const next = withDefaultWorkingHours(data?.data?.workingHours);
        setWorkingHours(next);
        return next;
      } catch (err) {
        console.error("Eroare salvare program:", err);
        setError(err.message);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [token, isAdmin, authHeaders]
  );

  useEffect(() => {
    if (token) {
      fetchWorkingHours().catch(() => {});
    } else {
      resetToDefault();
    }
  }, [token, fetchWorkingHours, resetToDefault]);

  const value = useMemo(
    () => ({
      workingHours,
      loading,
      saving,
      error,
      fetchWorkingHours,
      saveWorkingHours,
      canConfigure: Boolean(isAdmin),
    }),
    [workingHours, loading, saving, error, fetchWorkingHours, saveWorkingHours, isAdmin]
  );

  return <WorkingHoursContext.Provider value={value}>{children}</WorkingHoursContext.Provider>;
}

export const useWorkingHours = () => useContext(WorkingHoursContext);
